import React, { useState, useRef, useEffect } from 'react';
import Slider from 'react-slick';
import Modal from '@mui/material/Modal';

import './desktop-photo-review-detail-modal-style.scss';

import Typography from '@src/componentsNew/Typography/Typography';
import CloseIcon from '@src/componentsNew/Icons/Icons/CloseIcon';
import ThumbIcon from '@src/componentsNew/Icons/Icons/ThumbIcon';
import StarRatingIcon from '@src/componentsNew/Icons/StarRatingIcon';
import ChevronRightIcon from '@src/componentsNew/Icons/Icons/ChevronRightIcon';
import ChevronLeftIcon from '@src/componentsNew/Icons/Icons/ChevronLeftIcon';

import { reviews_reviews_edges_node } from '@molecules/ReviewList/__generated__/reviews';
import { getMaskedEmail } from '@utils';
import { formatDate } from '@src/utils';

interface IProps {
  open: boolean;
  onClose: () => void;
  review: reviews_reviews_edges_node;
  imageIdx: number;
  onClickVoteButton: (reviewId: string) => void;
}

interface ArrowIProps {
  onClick?: any;
  direction: 'next' | 'prev';
  currentIndex: number;
  totalSlideCount: number;
}

const ReviewCustomArrow = ({ onClick, direction, currentIndex, totalSlideCount }: ArrowIProps) => {
  if (direction === 'prev' && currentIndex === 0) {
    return null;
  }

  if (direction === 'next' && currentIndex === totalSlideCount - 1) {
    return null;
  }

  return (
    <div className={`review-arrow-wrapper ${direction}`} onClick={onClick}>
      {direction === 'next' ? (
        <ChevronRightIcon cName="custom-right-arrow" strokeColor="#FFFFFF" />
      ) : (
        <ChevronLeftIcon cName="custom-left-arrow" strokeColor="#FFFFFF" />
      )}
    </div>
  );
};

const DesktopPhotoReviewDetailModal = ({ open, onClose, review, imageIdx, onClickVoteButton }: IProps) => {
  const carouselRef = useRef();
  const [carouselIndex, setCarouselIndex] = useState(imageIdx);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        handleThumbnailClick(imageIdx);
      }, 0);
    }
  }, [open]);

  const handleThumbnailClick = (index: number) => {
    setCarouselIndex(index);
    // @ts-ignore
    carouselRef?.current?.slickGoTo(index);
  };

  const settings = {
    dots: true,
    infinite: false,
    arrows: true,
    nextArrow: (
      <ReviewCustomArrow
        direction="next"
        currentIndex={carouselIndex}
        totalSlideCount={review?.detailImages?.length || 0}
      />
    ),
    prevArrow: (
      <ReviewCustomArrow
        direction="prev"
        currentIndex={carouselIndex}
        totalSlideCount={review?.detailImages?.length || 0}
      />
    ),
    centerMode: true,
    centerPadding: '0',
    autoplay: false,
    autoplaySpeed: 4000,
    speed: 2000,
    slidesToShow: 1,
    slidesToScroll: 1,
    className: 'custom-review-slider',
    dotsClass: 'custom-dots',
    beforeChange: (_: number, next: number) => setCarouselIndex(next),
  };

  return (
    <Modal
      id="desktop-photo-review-detail-modal-container"
      open={open}
      onClose={onClose}
      onClick={(e) => e.stopPropagation()}
    >
      <div id="desktop-photo-review-detail-modal-content-wrapper">
        <div className="title-wrapper">
          <Typography cName="product-image-title" variant="subtitle1" component="span" style={{ color: '#090A0FDE' }}>
            Review
          </Typography>

          <CloseIcon cName="close-btn" onClick={onClose} />
        </div>

        <div className="photo-review-detail-content">
          <div className="review-image-area">
            <Slider ref={carouselRef} {...settings}>
              {review?.detailImages?.map((image) => (
                <img key={image.url} className="review-detail-carousel-img" src={image.url} />
              ))}
            </Slider>
          </div>

          <div className="review-content">
            <div className="review-info-top-area">
              <Typography variant="body3" component="span" style={{ color: '#11141D99' }}>
                {review.reviewerName || getMaskedEmail(review.reviewer.email)}
              </Typography>

              <div className="review-info-date">
                <div className="review-info-vote">
                  <ThumbIcon onClick={() => onClickVoteButton(review.id)} />

                  <Typography variant="caption" component="span" style={{ color: '#666666' }}>
                    {`helpful (${review.totalVotes})`}
                  </Typography>
                </div>

                <Typography variant="caption" component="span" style={{ color: '#666666' }}>
                  {formatDate(review.createdAt, '.')}
                </Typography>
              </div>
            </div>

            <div className="review-info-rating">
              <StarRatingIcon />
              <Typography variant="subtitle2" component="span" style={{ color: '#212121' }}>
                {Number(review.rating).toFixed(1)}
              </Typography>
            </div>

            <Typography cName="review-info-comment" variant="body3" component="p" style={{ color: '#11141D99' }}>
              {review.comment}
            </Typography>

            <div className="review-info-images-preview">
              {review?.detailImages?.map((image, idx) => (
                <img
                  key={image.url}
                  className={`review-detail-carousel-img ${idx === carouselIndex ? 'active' : ''}`}
                  src={image.url}
                  onClick={() => handleThumbnailClick(idx)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default DesktopPhotoReviewDetailModal;
